import { isNil } from 'lodash';
import React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import {
  ALLOCATIONS_SNAPPED_MESSAGE,
  type BlockId,
  isProFormaBlockOrMetricBenchmark,
  rangeDebugQuery,
  shouldShowAllocationsOutdatedWarning,
  type StudioRequestSubject,
  subjectAsOfDate,
} from 'venn-state';
import { Flexbox, GetColor, Icon, Tooltip } from 'venn-ui-kit';
import { getSubjectFromRequestSubject } from 'venn-utils';

type HistoricalPortfolioStudioHeaderTooltipsProps = {
  blockId: BlockId;
  subject: StudioRequestSubject;
  isBenchmark: boolean;
};

export const HistoricalPortfolioStudioHeaderTooltips = ({
  blockId,
  subject,
  isBenchmark,
}: HistoricalPortfolioStudioHeaderTooltipsProps) => {
  const rawSubject = getSubjectFromRequestSubject(subject);
  const rangeDebug = useRecoilValue(rangeDebugQuery(rawSubject));
  const historicalPortfolioMetadata = rangeDebug?.historicalPortfolioMetadata;
  const asOfDate = useRecoilValue(subjectAsOfDate([blockId, rawSubject]));
  let useProFormaForHistoricalBenchmarksBlock = useRecoilValue(isProFormaBlockOrMetricBenchmark(blockId));
  useProFormaForHistoricalBenchmarksBlock = isBenchmark && useProFormaForHistoricalBenchmarksBlock;

  if (isNil(historicalPortfolioMetadata)) {
    return null;
  }

  return (
    <Flexbox direction="row" gap={4}>
      {historicalPortfolioMetadata.isAllocationsForwardFilled && (
        <Tooltip usePortal content={ALLOCATIONS_SNAPPED_MESSAGE}>
          <AllocationsSnappedIcon iconLabel="allocations snapped icon" />
        </Tooltip>
      )}
      {shouldShowAllocationsOutdatedWarning(
        asOfDate,
        historicalPortfolioMetadata.allSortedAllocationDates,
        isBenchmark,
        useProFormaForHistoricalBenchmarksBlock,
      ) && (
        <Tooltip
          usePortal
          content="Most recent updated portfolio allocations are more than 6 months out of date and have drifted."
        >
          <AllocationsOutdatedIcon iconLabel="allocations outdated icon" />
        </Tooltip>
      )}
    </Flexbox>
  );
};

export const AllocationsSnappedIcon = styled(Icon).attrs({ type: 'square-info' })`
  color: ${GetColor.GreyScale.Grey80};
`;

export const AllocationsOutdatedIcon = styled(Icon).attrs({ type: 'diamond-exclamation' })`
  color: ${GetColor.Orange};
`;
